import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DesignShowcase: React.FC = () => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setLoading(false);
    setError(true);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || prev));
  };

  // PDF file path - user should place their PDF in public/assets/
  const pdfPath = '/assets/design-showcase.pdf';

  return (
    <section id="design" style={{ padding: '8rem 0', background: 'linear-gradient(180deg, #0a0e27 0%, #000000 100%)' }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1.5rem' }}>
        {/* Header */}
        <motion.div
          style={{ textAlign: 'center', marginBottom: '5rem' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p style={{ color: '#00d4ff', fontWeight: '600', fontSize: '0.875rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Design Portfolio
          </p>
          <h2 style={{
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: '800',
            color: '#ffffff',
            marginBottom: '1.5rem',
            lineHeight: '1.1',
            letterSpacing: '-0.02em'
          }}>
            Our Design Showcase
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '48rem',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Explore our collection of design work, featuring creative solutions and visual identities
            crafted for our clients.
          </p>
        </motion.div>

        {/* PDF Viewer */}
        <motion.div
          className="pdf-viewer-enhanced"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, type: 'spring', stiffness: 100 }}
        >
          {error ? (
            <div className="pdf-viewer-error">
              <p className="pdf-error-text">
                PDF not found. Please add your design showcase PDF to:
              </p>
              <code className="pdf-error-path">/public/assets/design-showcase.pdf</code>
              <p className="pdf-error-hint">
                You can replace this with any PDF showcasing your design work.
              </p>
            </div>
          ) : (
            <>
              <div className="pdf-viewer-wrapper">
                {loading && (
                  <div className="pdf-loading">
                    <div className="pdf-loading-spinner"></div>
                    <p className="pdf-loading-text">Loading design showcase...</p>
                  </div>
                )}

                <Document
                  file={pdfPath}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<div></div>}
                  className="pdf-document"
                >
                  <Page
                    pageNumber={pageNumber}
                    className="pdf-page"
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    width={typeof window !== 'undefined' ? Math.min(window.innerWidth * 0.8, 900) : 900}
                  />
                </Document>
              </div>

              {/* PDF Controls */}
              {numPages && !loading && (
                <div className="pdf-controls">
                  <button
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1}
                    className="pdf-control-btn"
                  >
                    ← Previous
                  </button>

                  <div className="pdf-page-info">
                    <span className="pdf-page-number">
                      Page {pageNumber} of {numPages}
                    </span>
                  </div>

                  <button
                    onClick={goToNextPage}
                    disabled={pageNumber >= numPages}
                    className="pdf-control-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>

        {/* CTA Section */}
        {!error && (
          <motion.div
            style={{ textAlign: 'center', marginTop: '4rem' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1.5rem', fontSize: '1.125rem' }}>
              Interested in our design services?
            </p>
            <button
              className="projects-cta-btn"
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Let's Work Together →
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default DesignShowcase;
